import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  CircularProgress,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { CodeEditor } from './code-editor';
import { TaskDescription } from './task-description';
import { TestsOutput } from './tests-output';
import { TestCase } from './types';

type LiveCodingContentPanelsProps = {
  timeLeft: number | null;
  code: string;
  isRunning: boolean;
  taskDescription: string;
  handleRun: () => void;
  handleSubmit: () => void;
  language: string;
  handleCodeChange: (code: string) => void;
  testResults: TestCase[];
  currentTestTab: number;
  setCurrentTestTab: (tab: number) => void;
  timeColor: string;
  formatTime: (seconds: number) => string;
  consoleOutput: string;
  isLoadingCode: boolean;
  submissionCompleted: boolean;
  isSubmitting: boolean;
  hasUnviewedConsoleOutput: boolean;
  setHasUnviewedConsoleOutput: (hasUnviewed: boolean) => void;
};

export const LiveCodingContentPanels = ({
  timeLeft,
  code,
  isRunning,
  taskDescription,
  handleRun,
  handleSubmit,
  language,
  handleCodeChange,
  testResults,
  currentTestTab,
  setCurrentTestTab,
  timeColor,
  formatTime,
  consoleOutput,
  isLoadingCode,
  submissionCompleted,
  isSubmitting,
  hasUnviewedConsoleOutput,
  setHasUnviewedConsoleOutput,
}: LiveCodingContentPanelsProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <PanelGroup direction='horizontal'>
        {/* Left panel - Problem description */}
        <Panel defaultSize={50} minSize={30}>
          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
            }}
          >
            <TaskDescription description={taskDescription} />
          </Box>
        </Panel>

        {/* Horizontal resize handle */}
        <PanelResizeHandle>
          <Box
            sx={{
              width: '4px',
              height: '100%',
              backgroundColor: theme.palette.divider,
              cursor: 'col-resize',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />
        </PanelResizeHandle>

        {/* Right panel - Code editor and tests */}
        <Panel defaultSize={50} minSize={30}>
          <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Toolbar
              sx={{
                justifyContent: 'flex-end',
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ color: timeColor, fontSize: 20 }} />
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 600,
                      color: timeColor,
                      fontFamily: 'monospace',
                    }}
                  >
                    {formatTime(timeLeft ?? 0)}
                  </Typography>
                </Box>

                <Button
                  variant='outlined'
                  startIcon={
                    isRunning ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  onClick={handleRun}
                  disabled={
                    isRunning ||
                    isLoadingCode ||
                    submissionCompleted ||
                    isSubmitting
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 80,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      borderColor: theme.palette.primary.light,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? `${theme.palette.primary.main}15`
                          : theme.palette.action.hover,
                    },
                    '&:disabled': {
                      borderColor: theme.palette.action.disabled,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  {isRunning ? 'Running' : 'Run'}
                </Button>

                <Button
                  variant='contained'
                  startIcon={<SendIcon />}
                  color='secondary'
                  onClick={handleSubmit}
                  disabled={
                    isRunning ||
                    isLoadingCode ||
                    submissionCompleted ||
                    isSubmitting
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.success.dark,
                    },
                    boxShadow:
                      theme.palette.mode === 'dark' ? 'none' : undefined,
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </Toolbar>

            {/* Vertical split between code editor and tests */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <PanelGroup direction='vertical'>
                {/* Code editor */}
                <Panel defaultSize={60} minSize={20}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {isLoadingCode ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={32} sx={{ mb: 2 }} />
                          <Typography variant='body2' color='text.secondary'>
                            Loading code editor...
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <CodeEditor
                        value={code}
                        language={language}
                        onChange={handleCodeChange}
                      />
                    )}
                  </Box>
                </Panel>

                {/* Vertical resize handle */}
                <PanelResizeHandle>
                  <Box
                    sx={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: theme.palette.divider,
                      cursor: 'row-resize',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </PanelResizeHandle>

                {/* Test results */}
                <Panel defaultSize={40} minSize={20}>
                  <Box sx={{ height: '100%' }}>
                    <TestsOutput
                      testCases={testResults}
                      currentTab={currentTestTab}
                      onTabChange={setCurrentTestTab}
                      consoleOutput={consoleOutput}
                      hasNewConsoleOutput={hasUnviewedConsoleOutput}
                      setHasUnviewedConsoleOutput={setHasUnviewedConsoleOutput}
                    />
                  </Box>
                </Panel>
              </PanelGroup>
            </Box>
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
};
